import { Link, useLocation, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { getPlatformApprovalDetails } from "../api/super_admin_jobs_api";
import { formatDate } from "../../../utils/date";
import { formatDateTime } from "../../../utils/datetime";

export const SuperAdminPendingApprovalJobDetails = () => {
  const locationState = useLocation();
  const { id } = useParams();

  const [jobDetails, setJobDetails] = useState<Record<string, unknown>>(
    (locationState.state || {}) as Record<string, unknown>
  );

  useEffect(() => {
    if (!id) return;

    const fetchDetails = async () => {
      try {
        const response = await getPlatformApprovalDetails(id);

        const data = response.data;

        setJobDetails({
          ...data,
          ...data.job,
          ...data.company,

          job_title: data.job?.title,
          job_description: data.job?.description,
          company_name: data.company?.name,
          requirements: data.job?.requirements,
          responsibilities: data.job?.responsibilities,
          benefits: data.job?.benefits,
          department: data.job?.department,
          location: data.job?.location,
          job_type: data.job?.job_type,
          work_mode: data.job?.work_mode,
          experience_level: data.job?.experience_level,
          salary_min: data.job?.salary_min,
          salary_max: data.job?.salary_max,
          salary_currency: data.job?.salary_currency,
          posting_visibility: data.job?.posting_visibility,
          application_deadline: data.job?.application_deadline,
          salary_period: data.job?.salary_period,
          hiring_urgency: data.job?.hiring_urgency,
          interview_process: data.job?.interview_process,
          vacancies: data.job?.vacancies,
          visa_sponsorship: data.job?.visa_sponsorship,
          relocation_offered: data.job?.relocation_offered,
          travel_percentage: data.job?.travel_percentage,
          target_start_date: data.job?.target_start_date,
          education_level: data.job?.education_level,
          created_by_company_user: data.job?.created_by_company_user,
          created_at: data.job?.created_at,
          skills: data.job?.skills,
        });
      } catch (error) {
        console.error("Failed to fetch approval details", error);
      }
    };

    fetchDetails();
  }, [id]);
  const title = getDisplayValue(jobDetails.job_title || jobDetails.title);
  const status = getDisplayValue(jobDetails.status);
  const companyName = getDisplayValue(jobDetails.company_name);
  const location = getDisplayValue(jobDetails.location);
  const jobDescription = getDisplayValue(jobDetails.job_description);
  const requirements = getDisplayValue(jobDetails.requirements);
  const skills = Array.isArray(jobDetails.skills)
    ? (jobDetails.skills as unknown[]).flat()
    : [];
  const hasJobDetails = Object.keys(jobDetails).length > 0;
  const hiddenFields = new Set([
    "job_id",
    "request_id",
    "company_id",
    "reviewed_by_id",

    // Already displayed separately
    "job_description",
    "requirements",
    "skills",

    // Hide nested API objects
    "job",
    "company",

    // Hide duplicated IDs
    "id",
  ]);
  const allFields = Object.entries(jobDetails).filter(
    ([key]) => !hiddenFields.has(key),
  );
  return (
    <div className="px-4 sm:px-6 lg:px-8 py-4 sm:py-6 max-w-7xl mx-auto">
      <nav className="flex mb-6" aria-label="Breadcrumb">
        <ol className="inline-flex items-center space-x-1 md:space-x-3">
          <li className="inline-flex items-center">
            <Link
              to="/super-admin/jobS"
              className="inline-flex items-center text-sm font-medium text-gray-700 hover:text-indigo-600"
            >
              Pending Jobs
            </Link>
          </li>
          <li>
            <div className="flex items-center">
              <svg
                className="w-6 h-6 text-gray-400"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                  clipRule="evenodd"
                />
              </svg>
              <span className="ml-1 text-sm font-medium text-gray-500 md:ml-2">
                {title || "Pending Job Details"}
              </span>
            </div>
          </li>
        </ol>
      </nav>
      <div className="min-h-screen bg-gray-50">
        <div className="mx-auto max-w-5xl px-4 py-8">
          {/* Top bar */}

          {/* Header */}
          <div className="bg-white rounded-lg border shadow-sm p-6">
            <div className="flex items-center justify-between gap-6">
              {/* Left Section */}
              <div className="flex items-center gap-4">
                <div className="h-14 w-14 flex items-center justify-center bg-blue-600 text-white rounded-xl text-xl font-bold uppercase">
                  {title ? title.charAt(0) : ""}
                </div>

                <div>
                  <div className="flex items-center gap-3">
                    <h1 className="text-2xl font-semibold">
                      {title || "Pending Job Details"}
                    </h1>

                    {status && (
                      <span className="px-3 py-1 text-xs rounded-full bg-yellow-100 text-yellow-700 font-medium">
                        {status}
                      </span>
                    )}
                  </div>

                  <div className="mt-2 text-sm text-gray-500 flex flex-wrap gap-x-4 gap-y-1">
                    {companyName && <div>Company: {companyName}</div>}
                    {location && <div>Location: {location}</div>}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Description */}
          {(jobDescription || requirements || skills) && (
            <div className="bg-white rounded-lg border shadow-sm p-6 mt-6">
              {jobDescription && (
                <>
                  <h2 className="text-md font-semibold mb-2">Job Description</h2>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap break-words">
                    {jobDescription}
                  </p>
                </>
              )}

              {requirements && (
                <div className="mt-6">
                  <h2 className="text-md font-semibold mb-2">Requirements</h2>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap break-words">
                    {requirements}
                  </p>
                </div>
              )}

              {skills.length > 0 && (
                <div className="mt-6">
                  <h2 className="text-md font-semibold mb-3">Skills</h2>

                  <div className="flex flex-wrap gap-2">
                    {skills.map((skill: any) => (
                      <span
                        key={skill.id}
                        className="px-3 py-1 rounded-full bg-blue-100 text-blue-700 text-sm font-medium"
                      >
                        {skill.skill_name}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="bg-white rounded-lg border shadow-sm p-6 mt-6">
            <h2 className="text-md font-semibold mb-4">All Fields</h2>
            {hasJobDetails ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {allFields.filter(
                  ([key]) =>
                    !["job_type", "work_mode", "posting_visibility"].includes(key)
                ).map(([key, value]) => (
                  <MetaItem
                    key={key}
                    fieldKey={key}
                    label={formatFieldLabel(key)}
                    value={value}
                  />
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500">
                Job details are unavailable. Return to Pending Jobs and open the
                job again.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
function MetaItem({ label, value, fieldKey }: { label: string; value: unknown; fieldKey?: string }) {
  return (
    <div className="min-w-0 rounded-md border border-gray-100 bg-gray-50 p-3">
      <p className="text-gray-400 text-xs">{label}</p>
      <p className="font-medium text-gray-800 whitespace-pre-wrap break-words">
        {formatFieldValue(value, fieldKey)}
      </p>
    </div>
  );
}

function getDisplayValue(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  return "";
}

function formatFieldLabel(key: string): string {
  return key
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase())
    .replace(/\bId\b/g, "ID")
    .replace(/\bIds\b/g, "IDs");
}

function formatFieldValue(value: unknown, fieldKey?: string): string {
  if (value === null || value === undefined || value === "") return "-";
  if (fieldKey === "application_deadline" && typeof value === "string") {
    return formatDate(value);
  }
  if (
    fieldKey === "skills" ||
    fieldKey === "required_skills" ||
    fieldKey === "preferred_skills"
  ) {
    if (Array.isArray(value)) {
      return value
        .map((item) => {
          if (typeof item === "object" && item !== null) {
            const skillName =
              (item as any).skill_name || (item as any).name || "Unknown";
            const category =
              (item as any).skill_category || (item as any).skill?.category || "general";
            const isRequired = (item as any).is_required === true ? "Yes" : "No";
            const minimumYears = (item as any).minimum_years ?? 0;
            const proficiency = (item as any).proficiency_level || "N/A";
            return [
              `Skill Name: ${skillName}`,
              `Category: ${category}`,
              `Required: ${isRequired}`,
              `Minimum Years: ${minimumYears}`,
              `Proficiency: ${proficiency}`,
            ].join("\n");
          }
          return String(item);
        })
        .join("\n\n");
    }
    return String(value);
  }
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "string") {
    const isoRegex =
      /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?$/;

    if (isoRegex.test(value)) {
      return formatDateTime(value);
    }

    return value;
  }
  if (typeof value === "string" || typeof value === "number") {
    return String(value);
  }

  if (typeof value === "string") {
    return formatDisplayText(value);
  }

  return JSON.stringify(value, null, 2);
}


function formatDisplayText(text: string): string {
  if (!text) return "-";

  return text
    // Convert camelCase -> camel Case
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    // Convert snake_case and kebab-case -> spaces
    .replace(/[_-]/g, " ")
    // Collapse multiple spaces
    .replace(/\s+/g, " ")
    .trim()
    // Convert to Title Case
    .replace(/\b\w/g, (char) => char.toUpperCase());
}